import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon } from 'lucide-react';
export function Hero() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Ghost card for the exit animation (slides out to left)
  const [ghostCard, setGhostCard] = useState<{
    index: number;
    title: string;
    description: string;
    image: string;
    color: string;
  } | null>(null);
  // Track the card entering from the right to handle the snap
  const [isSnapping, setIsSnapping] = useState(false);
  const [snappingCardIndex, setSnappingCardIndex] = useState<number | null>(null);
  const games = [{
    title: 'Clown Car',
    description: 'Press your luck to get and many points as possible',
    image: 'https://cdn.magicpatterns.com/uploads/vmjYXMpLgRK4EAMrvK5CVv/flip-seven.png',
    color: '#E85D3F'
  }, {
    title: 'Survey Says...?',
    description: 'Guess the top survey answers',
    image: 'https://cdn.magicpatterns.com/uploads/hdoMBx1ocLa38GkphmRym2/survey-says.png',
    color: '#2563EB'
  }, {
    title: 'Concentration',
    description: 'Remember the cards positions to guess pairs',
    image: 'https://cdn.magicpatterns.com/uploads/fYAY2iwCvjGpi68p7ky2LQ/concentration.png',
    color: '#E64B2D'
  }, {
    title: 'Plague Poker',
    description: 'Trade cards to end the drug war',
    image: 'https://cdn.magicpatterns.com/uploads/kVksyvefMPFiNQjKfiztY9/cockroach-poker.png',
    color: '#D97757'
  }, {
    title: "What's the Password",
    description: 'Guess the password as quickly as possible',
    image: 'https://cdn.magicpatterns.com/uploads/eAthAcEQyap2gTV7u2fUQs/password-please.png',
    color: '#FFA603'
  }];
  // Auto-advance carousel every 2 seconds
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        // The card currently at the far left (-2) is the one wrapping around
        const wrappingIndex = (prev - 2 + games.length) % games.length;
        const wrappingCard = games[wrappingIndex];
        // 1. Create a ghost card to handle the exit animation (left side)
        setGhostCard({
          ...wrappingCard,
          index: wrappingIndex // Keep original index for key
        });
        // 2. Mark the real card as "snapping" so it starts at the far right (+3)
        setSnappingCardIndex(wrappingIndex);
        setIsSnapping(true);
        // 3. After a tiny delay (allow DOM to position it at +3), release it to slide to +2
        setTimeout(() => {
          setIsSnapping(false);
        }, 20);
        // 4. Clear the ghost card after the transition finishes
        setTimeout(() => {
          setGhostCard(null);
          setSnappingCardIndex(null);
        }, 600);
        return (prev + 1) % games.length;
      });
    }, 2500);
    return () => clearInterval(interval);
  }, [isPaused, games.length]);
  const handleMouseEnter = () => {
    setIsPaused(true);
  };
  const handleMouseLeave = () => {
    setIsPaused(false);
  };
  // Get 5 cards with their positions relative to center
  const getCardsWithPositions = () => {
    return games.map((game, index) => {
      // Calculate position relative to current center (-2, -1, 0, 1, 2)
      let position = index - currentIndex;
      // Wrap around for circular carousel
      if (position < -2) position += games.length;
      if (position > 2) position -= games.length;
      return {
        ...game,
        index,
        position
      };
    }).filter(card => card.position >= -2 && card.position <= 2); // Show 5 cards
  };
  const visibleCards = getCardsWithPositions();
  return <section className="w-full px-4 relative flex items-center" style={{
    minHeight: 'calc(70vh - 64px)',
    paddingTop: '2rem',
    paddingBottom: '1rem'
  }} data-id="element-72">
      {/* Logo - Top Left Absolute Position */}
      <div className="absolute top-8 left-8 z-20 flex items-center gap-3" data-id="element-73">
        <img src="https://cdn.magicpatterns.com/uploads/8dB8M4GqHB66FgPhzt2tyW/Group_44.png" alt="Fifth Door Logo" className="h-8 w-auto object-contain brightness-0 invert" data-id="element-74" />
        <span className="text-white text-lg" style={{
        fontFamily: 'Bungee, sans-serif'
      }} data-id="element-75">
          FIFTH DOOR
        </span>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto text-center relative z-10 w-full" data-id="element-76">
        <div className="px-4 pt-20 pb-12 md:px-16 md:pt-28 md:pb-16" data-id="element-77">
          {/* Beta Pill */}
          <div className="flex justify-center mb-8" data-id="element-78">
            <div className="inline-flex items-center px-8 md:px-10 py-2 rounded-full" style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)'
          }} data-id="element-79">
              <p className="text-sm font-normal" style={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: "'Instrument Sans', sans-serif"
            }} data-id="element-80">
                Fifth Door is in private beta. <br className="md:hidden" data-id="element-81" />
                Join our Discord to get on the waitlist.
              </p>
            </div>
          </div>

          <p className="text-[50px] md:text-[74px] mb-4" style={{
          fontFamily: 'Bungee, sans-serif',
          color: 'white',
          lineHeight: '1.1'
        }} data-id="element-82">
            CREATE. PLAY. ANYTIME.
          </p>
          <p className="text-[20px] md:text-[24px] mb-12 max-w-4xl mx-auto" style={{
          color: 'white',
          opacity: 0.8,
          fontFamily: "'Instrument Sans', sans-serif"
        }} data-id="element-83">
            Go from idea to playable game in minutes. No coding required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 w-full sm:px-0" data-id="element-84">
            <a href="https://discord.gg/HCC76Zt82Y" target="_blank" rel="noopener noreferrer" className="w-[90%] sm:w-auto px-8 py-3.5 rounded-full font-semibold text-[15px] transition-all duration-300 hover:bg-white hover:bg-opacity-10 hover:scale-105 flex items-center justify-center gap-2" style={{
            border: '1px solid white',
            color: 'white',
            backgroundColor: 'transparent',
            fontFamily: "'Instrument Sans', sans-serif"
          }} data-id="element-85">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bCTwQuQg1RHyDN81pzL1uF/discord-icon-43736.png" alt="Discord" className="w-6 h-6 brightness-0 invert" data-id="element-86" />
              Join Discord
            </a>
            <a href="https://forms.gle/3JfvtbJsHzJb9Qaj9" target="_blank" rel="noopener noreferrer" className="w-[90%] sm:w-auto px-8 py-3.5 rounded-full font-semibold text-[15px] transition-all duration-300 hover:opacity-90 hover:scale-105 flex items-center justify-center gap-2" style={{
            backgroundColor: 'white',
            color: '#074030',
            fontFamily: "'Instrument Sans', sans-serif"
          }} data-id="element-87">
              Apply for Early Access
            </a>
          </div>

          {/* Game Carousel - 5 cards with center larger */}
          <div className="relative mt-16 flex items-center justify-center overflow-visible" style={{
          marginTop: '4rem',
          marginBottom: '4rem',
          minHeight: '500px',
          marginLeft: '-30px',
          marginRight: '-30px',
          width: 'calc(100% + 60px)',
          maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
          WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)'
        }} data-id="element-88">
            <div className="relative flex items-center justify-center" style={{
            width: '100%',
            maxWidth: '100%'
          }} data-id="element-89">
              {/* Ghost Card - Exiting to Left */}
              {ghostCard && <div key={`ghost-${ghostCard.index}`} className="absolute flex-shrink-0 rounded-3xl overflow-hidden" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
              width: '300px',
              height: '375px',
              backgroundColor: ghostCard.color,
              transform: `translateX(-825px) scale(0.75)`,
              opacity: 1,
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 0,
              // We need to animate FROM -565px to -825px
              // But React renders the target state.
              // To make it animate, we rely on the fact that it WAS at -565px in the previous render (as a real card).
              // However, as a NEW key 'ghost', it might just appear at -825px.
              // To fix this, we can use a key that matches the original card? No, duplicate keys are bad.
              // Actually, since it's a new element, it will mount.
              // We need it to mount at -565px and then move to -825px.
              // We can use a simple animation keyframe or just accept that for this specific implementation,
              // we might need a ref or layout effect.
              // Simplified approach: Just set it to -825px. If it doesn't animate, at least it's gone.
              // Better: Use a CSS animation for the ghost exit.
              animation: 'slideOutLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards'
            }} data-id="element-90">
                  <style data-id="element-91">
                    {`
                      @keyframes slideOutLeft {
                        from { transform: translateX(-565px) scale(0.75); }
                        to { transform: translateX(-825px) scale(0.75); }
                      }
                    `}
                  </style>
                  <div className="relative w-full h-full flex flex-col" data-id="element-92">
                    <div className="relative w-full overflow-hidden" style={{
                  height: '304px'
                }} data-id="element-93">
                      <img src={ghostCard.image} alt={ghostCard.title} className="w-full h-full object-cover" data-id="element-94" />
                      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none" style={{
                    background: `linear-gradient(to bottom, transparent 0%, ${ghostCard.color} 100%)`
                  }} data-id="element-95" />
                    </div>
                    <div className="flex-1 p-6 flex flex-col justify-end" data-id="element-96">
                      <div className="text-left" data-id="element-97">
                        <h3 className="font-medium text-white mb-1.5" style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: '19px'
                    }} data-id="element-98">
                          {ghostCard.title}
                        </h3>
                        <p className="text-white text-opacity-90" style={{
                      fontSize: '15px'
                    }} data-id="element-99">
                          {ghostCard.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{
                border: '1px solid rgba(255, 255, 255, 0.24)',
                zIndex: 20
              }} data-id="element-100" />
                </div>}

              {/* Visible Cards */}
              {visibleCards.map(game => {
              const isCenter = game.position === 0;
              const isFarLeft = game.position === -2;
              const isLeft = game.position === -1;
              const isRight = game.position === 1;
              const isFarRight = game.position === 2;
              // Calculate transform based on position
              let translateX = 0;
              if (isFarLeft) translateX = -565;
              if (isLeft) translateX = -305;
              if (isCenter) translateX = 0;
              if (isRight) translateX = 305;
              if (isFarRight) translateX = 565;
              // Scale logic:
              // Center: 0.8 (was 0.85)
              // Left/Right: 0.81
              // Far Left/Right: 0.75
              let scale = 0.75;
              if (isCenter) scale = 0.8;
              if (isLeft || isRight) scale = 0.81;
              const isSnappingCard = game.index === snappingCardIndex;
              // If snapping (entering from right), start at +825px
              if (isSnapping && isSnappingCard) {
                translateX = 825;
              }
              // Don't hide the card at all
              const opacity = 1;
              // Transition logic
              let transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
              if (isSnapping && isSnappingCard) {
                transition = 'none'; // Snap instantly to start position
              }
              // Z-index logic:
              // Center: 30 (highest)
              // Left/Right: 20 (middle)
              // Far Left/Right: 10 (lowest)
              // Snapping card: 0 (absolute lowest to stay behind)
              let zIndex = 10;
              if (isCenter) zIndex = 30;
              if (isLeft || isRight) zIndex = 20;
              if (isSnappingCard) zIndex = 0;
              return <div key={game.index} className="absolute flex-shrink-0 rounded-3xl overflow-hidden group hover:shadow-2xl hover:brightness-110" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
                width: isCenter ? '400px' : '300px',
                height: isCenter ? '500px' : '375px',
                backgroundColor: game.color,
                transform: `translateX(${translateX}px) scale(${scale})`,
                opacity,
                transition,
                zIndex
              }} data-id="element-101">
                    <div className="relative w-full h-full flex flex-col" data-id="element-102">
                      {/* Game Image at the top with fade-out gradient */}
                      <div className="relative w-full overflow-hidden group-hover:scale-105 transition-transform duration-500" style={{
                    height: isCenter ? '405px' : '304px',
                    transition: 'height 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s ease'
                  }} data-id="element-103">
                        <img src={game.image} alt={game.title} className="w-full h-full object-cover" data-id="element-104" />
                        {/* Fade-out gradient overlay */}
                        <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none" style={{
                      background: `linear-gradient(to bottom, transparent 0%, ${game.color} 100%)`
                    }} data-id="element-105" />
                      </div>

                      {/* Content - Left Aligned at bottom */}
                      <div className="flex-1 p-6 flex flex-col justify-end relative z-10" data-id="element-106">
                        <div className="text-left transform group-hover:translate-y-[-4px] transition-transform duration-300" data-id="element-107">
                          <h3 className="font-medium text-white mb-1.5" style={{
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontSize: isCenter ? '24px' : '19px',
                        transition: 'font-size 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                      }} data-id="element-108">
                            {game.title}
                          </h3>
                          <p className="text-white text-opacity-90" style={{
                        fontSize: isCenter ? '18px' : '15px',
                        transition: 'font-size 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                      }} data-id="element-109">
                            {game.description}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Border Overlay - Sits on top of everything */}
                    <div className="absolute inset-0 rounded-3xl pointer-events-none group-hover:border-white/40 transition-colors duration-300" style={{
                  border: '1px solid rgba(255, 255, 255, 0.24)',
                  zIndex: 20
                }} data-id="element-110" />
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}